/**
 * The kill feed, top-right (B3 · Halo + WoW bar).
 *
 * Call of Duty rows: KILLER · weapon glyph · VICTIM, with an HS mark when the Shot event
 * in the same tick said headshot. Colour carries the whole read and never repeats itself
 * in words:
 *
 *   mint     you
 *   cyan     an ally — another Waker
 *   magenta  a Hymn
 *   red      a Waker who just died. Dead is red, it always will be — and it outranks
 *            "this was one of mine", so your own death is a red row, not a mint one.
 *
 * Five rows are built once at boot and reused forever: a kill writes text and classes into
 * the pool, never a node, and the six-second expiry walks the pool by index rather than
 * copying `children` into a fresh array every frame.
 */

import { icon, weaponIcon } from './icons.js';
import { WEAPON_FALL } from '@stillsun/shared';

/** Rows on screen at once. The sixth kill retires the oldest. */
const MAX_ROWS = 5;
/** A row holds for this long, then fades over FADE_MS and goes. */
const ROW_TTL_MS = 6000;
const FADE_MS = 400;

/** What the feed needs to know about one Ev.Death. */
export interface KillEvent {
  killerId: number;
  victimId: number;
  weaponId: number;
  headshot: boolean;
}

/** How the feed resolves the ids it is handed. All of it lives in main.ts already. */
export interface KillFeedHost {
  /** The local player's id, so "you" can be told from everybody else. */
  selfId: () => number;
  /** Display name for an id — RemoteManager.nameOf, plus the local name for selfId. */
  nameOf: (id: number) => string;
  /** True for an id in the entity space: a Hymn, a turret, the Verger. */
  isEntity: (id: number) => boolean;
}

/** Who a name belongs to. Drives the row's colour, and nothing else does. */
type Kind = 'you' | 'ally' | 'hymn' | 'none';

interface Row {
  root: HTMLDivElement;
  killer: HTMLSpanElement;
  weapon: HTMLSpanElement;
  hs: HTMLSpanElement;
  victim: HTMLSpanElement;
  /** performance.now() the row starts fading at; 0 when the row is free. */
  until: number;
  /** Last weapon glyph drawn here, so an unchanged weapon costs no innerHTML. */
  lastWeapon: number;
}

export class KillFeed {
  private readonly rows: Row[] = [];
  private readonly host: KillFeedHost;

  constructor(root: HTMLElement, host: KillFeedHost) {
    this.host = host;
    for (let i = 0; i < MAX_ROWS; i++) {
      const el = document.createElement('div');
      el.className = 'kf off';
      const killer = document.createElement('span');
      killer.className = 'who';
      const weapon = document.createElement('span');
      weapon.className = 'wpn';
      const hs = document.createElement('span');
      hs.className = 'hs';
      hs.textContent = 'HS';
      hs.hidden = true;
      const victim = document.createElement('span');
      victim.className = 'who';
      el.append(killer, weapon, hs, victim);
      root.append(el);
      this.rows.push({ root: el, killer, weapon, hs, victim, until: 0, lastWeapon: -1 });
    }
  }

  /**
   * One death. The oldest row is recycled when all five are live, so the feed is always
   * newest-last and never allocates a node.
   */
  add(ev: KillEvent): void {
    const row = this.take();
    if (!row) return;
    const self = this.host.selfId();
    const now = performance.now();

    // A killerId of 0, or a killer who is also the victim, is the deck itself: a fall, a
    // hazard panel, the Band. There is nobody to name, so the row starts at the glyph.
    const noKiller = ev.killerId === 0 || ev.killerId === ev.victimId;
    const killerKind = noKiller ? 'none' : this.kindOf(ev.killerId, self);
    const victimKind = this.kindOf(ev.victimId, self);
    // A Waker dying is red, whoever did it and whoever it was.
    const victimDead = victimKind === 'you' || victimKind === 'ally';
    const mine = ev.killerId === self && ev.victimId !== self;

    row.killer.textContent = noKiller ? '' : this.host.nameOf(ev.killerId);
    row.killer.className = noKiller ? 'who' : `who ${killerKind}`;
    row.killer.hidden = noKiller;

    // The weapon that did it, as a family glyph. Weapon 0 (and anything the icon set has
    // not been taught) is the crossed pair: the deck, the fall, the Band.
    // A fall keeps its glyph (the deck is the killer, but the *how* is worth a picture).
    const wid = noKiller ? (ev.weaponId === WEAPON_FALL ? WEAPON_FALL : 0) : ev.weaponId;
    if (wid !== row.lastWeapon) {
      row.lastWeapon = wid;
      row.weapon.innerHTML = icon(weaponIcon(wid), 22);
    }

    row.hs.hidden = !ev.headshot;
    row.victim.textContent = this.host.nameOf(ev.victimId);
    row.victim.className = victimDead ? 'who dead' : `who ${victimKind}`;

    row.root.className = victimDead ? 'kf dead' : mine ? 'kf mine' : 'kf';
    row.until = now + ROW_TTL_MS;
  }

  /**
   * Expires stale rows. Called once per frame from the same place `tickEffects` is: walks
   * the fixed pool by index, so a frame with nothing to do costs five number compares.
   */
  tick(nowMs: number): void {
    for (const row of this.rows) {
      if (row.until === 0) continue;
      if (nowMs >= row.until + FADE_MS) {
        row.until = 0;
        row.root.className = 'kf off';
      } else if (nowMs >= row.until && !row.root.classList.contains('fade')) {
        row.root.classList.add('fade');
      }
    }
  }

  /** Leaving the raid: every row goes at once. */
  clear(): void {
    for (const row of this.rows) {
      row.until = 0;
      row.root.className = 'kf off';
    }
  }

  /**
   * A synthetic row, for screenshots and the console handle:
   * `stillsun.killFeed.debugRow({ killerId: 1, victimId: 2, weaponId: 1, headshot: true })`.
   */
  debugRow(ev: KillEvent): void {
    this.add(ev);
  }

  /**
   * The next free row, or the oldest live one. Rows are physically in DOM order, so a
   * recycled row has to move to the end to keep the feed reading newest-last.
   */
  private take(): Row | undefined {
    let free: Row | undefined;
    let oldest: Row | undefined;
    for (const row of this.rows) {
      if (row.until === 0) {
        free ??= row;
      } else if (!oldest || row.until < oldest.until) {
        oldest = row;
      }
    }
    const row = free ?? oldest;
    if (!row) return undefined;
    row.root.classList.remove('fade');
    row.root.parentElement?.append(row.root);
    return row;
  }

  /** Who this id is, from the caller's point of view. */
  private kindOf(id: number, self: number): Kind {
    if (id === self) return 'you';
    if (this.host.isEntity(id)) return 'hymn';
    if (id > 0) return 'ally';
    return 'none';
  }
}
